import React, { useState } from 'react'
import {
  MotifButton,
  MotifChip,
  MotifDropdownItem,
  MotifIcon,
  MotifIconButton,
  MotifDropdownPortal,
} from '@ey-xd/motif-react'
import moment from 'moment'
import {
  IconoirCalendar,
  MotifNavigationIcMoreVert24px,
} from '@ey-xd/motif-icon'
import { Link, useParams } from 'react-router-dom'
import {
  useDeleteApplicationMutation,
  useSaveApplicationMutation,
} from '@/services/application/applicationReducer'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store'
import { EngagementDetail } from '@/services/types'

const getStatusType = (statusType: number) => {
  if (statusType === 1) {
    return 'New'
  } else {
    return statusType === 3 ? 'Completed' : 'In Progress'
  }
}

const getActionType = (statusType: number) => {
  if (statusType === 1) {
    return 'Start'
  } else {
    return statusType === 2 ? 'Continue' : 'View'
  }
}

const StatusCellRenderer = (props: any) => {
  return (
    <MotifChip
      dotSide="left"
      title="Application Status"
      className={props.data.statusType === 1 ? 'newItem' : ''}
      type="chip"
      variant={
        props.data.statusType === 1
          ? undefined
          : props.data.statusType === 3
            ? 'success'
            : 'warning'
      }
    >
      {getStatusType(props.data.statusType)}
    </MotifChip>
  )
}

export const selectActiveHRListByEngagementId = createSelector(
  [
    (state: any) => state.HRList.HRList,
    (_: any, engagementId: string) => engagementId,
  ],
  (HRList, engagementId) =>
    HRList.filter(
      (item: any) =>
        item.engagementId === engagementId &&
        item.isActive &&
        item.mappingCompleted,
    )
)

const ActionCellRenderer = (props: any) => {
  const { engagementId } = useParams<{ engagementId: string }>()
  const activeHRList = useSelector((state: RootState) =>
    selectActiveHRListByEngagementId(state, engagementId ?? '')
  )
  const isArchived = useSelector(
    (state: { clientCard: { currentEngagement: EngagementDetail } }) =>
      state.clientCard.currentEngagement?.isArchived,
  )

  // archived apps may have no evidence left and the detail page surfaces personal HR data - block navigation entirely
  if (isArchived) {
    return null
  }

  return (
    <Link
      className="action-button"
      to={`application/${props.data.id}`}
      state={{ from: props.data.id, applicationName: props.data.applicationName }}
    >
      <MotifButton
        size="small"
        variant={props.data.statusType === 1 ? 'primary' : 'secondary'}
        disabled={props.data.statusType === 1 && activeHRList.length === 0}
        title={
          props.data.statusType === 1 && activeHRList.length === 0
            ? 'Please activate HR Document to start'
            : ''
        }
      >
        {getActionType(props.data.statusType)}
      </MotifButton>{' '}
    </Link>
  )
}

const KebabActionsRenderer = (props: any) => {
  const [isOpen, setIsOpen] = useState(false)
  const [saveApplication, { isLoading }] = useSaveApplicationMutation()
  const [deleteApplication, { isLoading: isDeleting }] =
    useDeleteApplicationMutation()
  const isArchived = useSelector(
    (state: { clientCard: { currentEngagement: EngagementDetail } }) =>
      state.clientCard.currentEngagement?.isArchived,
  )

  const handleDuplicate = async () => {
    const applicationData = {
      applicationName: props.data.applicationName,
      engagementId: props.data.engagementId,
      description: props.data.description,
    }
    try {
      await saveApplication(applicationData)
      props.refetch()
      setIsOpen(false)
    } catch (error) {
      console.error('Duplicate failed', error)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteApplication(props.data.id).unwrap()
      props.refetch()
    } catch (error) {
      console.error('Delete failed', error)
    }
    setIsOpen(false)
  }

  const handleEdit = () => {
    props.onEdit(props.data)
    setIsOpen(false)
  }

  if (isArchived) {
    return null
  }

  return (
    <MotifDropdownPortal
      id="dropdown-1"
      handleClickOutside={() => setIsOpen(false)}
      open={isOpen}
      trigger={
        <MotifIconButton onClick={() => setIsOpen(!isOpen)}>
          <MotifIcon
            iconFunction={MotifNavigationIcMoreVert24px}
            title="More options"
          />
        </MotifIconButton>
      }
    >
      <MotifDropdownItem onClick={handleDuplicate}>Duplicate</MotifDropdownItem>
      <MotifDropdownItem onClick={handleEdit}>Edit</MotifDropdownItem>
      <MotifDropdownItem onClick={handleDelete}>Delete</MotifDropdownItem>
    </MotifDropdownPortal>
  )
}

export const useColumnApplicationListDefs = ({
  refetch,
  onEdit,
}: {
  refetch: () => void
  onEdit: (data: any) => void
}) => {
  return [
    {
      comparator: () => 0,
      field: 'applicationName',
      type: 'applicationReferenceType',
      headerName: 'Information Technologies List',
      cellClass: 'text-wrap',
      wrapText: true,
      minWidth: 200,
      flex: 1,
      sortable: true,
      unSortIcon: true,
      cellRenderer: function (params: any) {
        return (
            <p
              style={{
                marginRight: '8px',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: 185,
              }}
              title={params.data.applicationName}
            >
              {params.data.applicationName}
            </p>
        )
      },
    },
    {
      comparator: () => 0,
      field: 'description',
      type: 'applicationDescReferenceType',
      headerName: 'Application Description',
      title: 'Application Description',
      minWidth: 300,
      flex: 1,
      cellRenderer: function (params: any) {
        return (
            <p
              style={{
                marginRight: '8px',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: 285,
              }}
              title={params.data.description}
            >
              {params.data.description}
            </p>
        )
      },
    },
    {
      comparator: () => 0,
      field: 'createdOn',
      type: 'createdDateReferenceType',
      headerName: 'Created Date',
      maxWidth: 150,
      flex: 1,
      headerComponent: () => (
        <div className="ag-header-cell-label">
          <span className="ag-header-cell-text">Created Date</span>&nbsp;
          <MotifIcon src={IconoirCalendar} fill="#000000" stroke="#000000" />
        </div>
      ),
      cellRenderer: function (params: any) {
        return moment(params.data.createdOn).format('MM/DD/YYYY')
      },
    },
    {
      comparator: () => 0,
      field: 'signOffDueDate',
      type: 'signOffDueDateReferenceType',
      headerName: 'Sign-off Due Date',
      maxWidth: 180,
      flex: 1,
      headerComponent: () => (
        <div className="ag-header-cell-label">
          <span className="ag-header-cell-text">Audit Report Date</span>&nbsp;
          <MotifIcon src={IconoirCalendar} fill="#000000" stroke="#000000" />
        </div>
      ),
      cellRenderer: function (params: any) {
        return moment(params.data.signOffDueDate).format('MM/DD/YYYY')
      },
    },
    {
      comparator: () => 0,
      field: 'statusType',
      type: 'statusTypeReferenceType',
      headerName: 'Status',
      sortable: true,
      unSortIcon: true,
      maxWidth: 150,
      flex: 1,
      cellRenderer: StatusCellRenderer,
    },
    {
      comparator: () => 0,
      field: 'statusType',
      type: 'statusTypeReferenceType',
      headerName: '',
      flex: 1,
      cellClass: 'align-left',
      cellRenderer: ActionCellRenderer,
    },
    {
      comparator: () => 0,
      field: 'statusType',
      type: 'statusTypeReferenceType',
      headerName: '',
      flex: 1,
      cellRenderer: (props: any) => (
        <KebabActionsRenderer {...props} refetch={refetch} onEdit={onEdit} />
      ),
    },
  ]
}
